import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertType, Icon } from '../../../components';
import { ACTIVE_TAB_PARAM, AppRoutes } from '../../../constants';
import { useGetUserPermissions, useSimActions, useToast } from '../../../hooks';
import { PracticeProspect, ProspectStatus, SimulationsFilterKeys, SimulationsTab, TextColor } from '../../../types';
import { conditionalArray, isProspectLocked } from '../../../utils';
import { SimActionType } from '../SimActionModals';

interface useSimulationActionsProps {
  prospect: PracticeProspect;
  closeActions: () => void;
  refetchProspects: () => void;
  setProspects: Dispatch<SetStateAction<PracticeProspect[]>>;
  setOpenSimModal: Dispatch<SetStateAction<{ type: SimActionType; prospect: PracticeProspect } | null>>;
}

const useSimulationActions = ({
  prospect,
  closeActions,
  refetchProspects,
  setProspects,
  setOpenSimModal,
}: useSimulationActionsProps) => {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get(ACTIVE_TAB_PARAM);

  const { associatedPhoneNumber, personaId, status, isDraft, isUsed, isFavorited } = prospect || {};
  const isArchived = status === ProspectStatus.ARCHIVED;

  const isInFavorites = activeTab === SimulationsTab.FAVORITES;

  const navigate = useNavigate();

  const { getCanUserEditSimInSelfService } = useGetUserPermissions();
  const canUserEditSimInSelfService = getCanUserEditSimInSelfService(
    prospect.category,
    prospect.isDraft,
    prospect.userId
  );

  const { runReactivate, runFavorite, runUnfavorite } = useSimActions();
  const { showToast } = useToast();

  const isLocked = isProspectLocked(prospect);

  // Navigates to the prospect page.
  const onEditProspect = useCallback(() => {
    navigate(`${AppRoutes.PROSPECT}/${personaId}`);
    closeActions();
  }, [personaId, navigate, closeActions]);

  // Copies the prospect's phone number to the clipboard.
  const onCopyPhoneNumber = useCallback(() => {
    if (!associatedPhoneNumber) return;

    navigator.clipboard.writeText(associatedPhoneNumber);
    showToast({ message: 'Phone number copied to clipboard', type: AlertType.SUCCESS });
    closeActions();
  }, [associatedPhoneNumber, showToast, closeActions]);

  // Copies the prospect's link to the clipboard.
  const onShare = useCallback(() => {
    const url = new URL(window.location.href);

    if (canUserEditSimInSelfService) {
      // If the user is allowed to view/edit the prospect, navigate to the prospect page.
      url.pathname = `${AppRoutes.PROSPECT}/${personaId}`;
    } else {
      // Otherwise, generate a link that filters the activity page to the prospect.
      url.search = '';
      url.searchParams.set(SimulationsFilterKeys.PROSPECT, personaId);
    }

    navigator.clipboard.writeText(url.toString());

    showToast({ message: 'Link to prospect copied to clipboard', type: AlertType.SUCCESS });
    closeActions();
  }, [canUserEditSimInSelfService, personaId, showToast, closeActions]);

  const onUnarchive = useCallback(() => {
    runReactivate({
      id: personaId,
      onSuccess: () => {
        // We refetch prospects after un-archiving to ensure the prospect is moved to the top of the list.
        // We cannot handle this on the FE because we only have access to the current page of prospects.
        refetchProspects();
        closeActions();
      },
    });
  }, [personaId, runReactivate, refetchProspects, closeActions]);

  const onDelete = useCallback(() => {
    setOpenSimModal({ type: isUsed ? SimActionType.ARCHIVE : SimActionType.DELETE, prospect });
    closeActions();
  }, [prospect, setOpenSimModal, closeActions, isUsed]);

  const onUnfavorite = useCallback(() => {
    runUnfavorite({
      id: personaId,
      onSuccess: () => {
        closeActions();
        // If we are in the favorites tab, remove the prospect from the list.
        if (isInFavorites) {
          setProspects((prev) => prev.filter((p) => p.personaId !== personaId));
        } else {
          setProspects((prev) => prev.map((p) => (p.personaId === personaId ? { ...p, isFavorited: false } : p)));
        }
      },
    });
  }, [personaId, runUnfavorite, closeActions, setProspects, isInFavorites]);

  const onFavorite = useCallback(() => {
    runFavorite({
      id: personaId,
      onSuccess: () => {
        closeActions();
        setProspects((prev) => prev.map((p) => (p.personaId === personaId ? { ...p, isFavorited: true } : p)));
      },
    });
  }, [personaId, runFavorite, closeActions, setProspects]);

  const activeActions = useMemo(
    () => [
      ...conditionalArray(canUserEditSimInSelfService, {
        label: 'Edit',
        icon: Icon.EDIT,
        onClick: onEditProspect,
      }),
      ...conditionalArray(!!associatedPhoneNumber && !isLocked, {
        label: 'Copy phone number',
        icon: Icon.COPY,
        onClick: onCopyPhoneNumber,
      }),
      // Draft simulations cannot be shared because they are private.
      ...conditionalArray(!isDraft, {
        label: 'Share',
        icon: Icon.SHARE,
        onClick: onShare,
      }),
      ...conditionalArray(isDraft, {
        label: 'Delete',
        icon: Icon.TRASH,
        onClick: onDelete,
        iconColor: TextColor.DESTRUCTIVE,
      }),
      ...conditionalArray(isFavorited && !isDraft, {
        icon: Icon.STAR,
        iconColor: 'stroke-orange-500',
        label: 'Remove from favorites',
        onClick: onUnfavorite,
      }),
      ...conditionalArray(!isFavorited && !isDraft, {
        icon: Icon.STAR,
        iconColor: 'stroke-orange-500 fill-orange-500',
        label: 'Add to favorites',
        onClick: onFavorite,
      }),
    ],
    [
      canUserEditSimInSelfService,
      associatedPhoneNumber,
      isLocked,
      onEditProspect,
      onCopyPhoneNumber,
      onShare,
      isDraft,
      isFavorited,
      onDelete,
      onUnfavorite,
      onFavorite,
    ]
  );

  const archivedActions = useMemo(
    () => [
      {
        label: 'Unarchive',
        icon: Icon.UNARCHIVE,
        onClick: onUnarchive,
      },
    ],
    [onUnarchive]
  );

  return isArchived ? archivedActions : activeActions;
};

export default useSimulationActions;
